/**
 * Schedule helpers shared by the New Job modal, the job view's scheduler and
 * the appointment form (GID-Session-012). Dates are Brisbane-local
 * 'YYYY-MM-DD' and times 'HH:MM' — the shapes the /schedule endpoint takes.
 */
const TZ = 'Australia/Brisbane';
export const DEFAULT_DAY_MINUTES = 480; // a job type with no set duration fills a working day when a span is scheduled
export const DEFAULT_VISIT_MINUTES = 60; // …and takes an hour as a one-off visit (the server's default footprint)

export const hmToMin = (hm) => {
  if (!hm) return null;
  const [h, m] = String(hm).split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : null;
};
export const minToHm = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/** end − start in minutes, or null when either is blank. */
export function minutesBetween(startHm, endHm) {
  const a = hmToMin(startHm);
  const b = hmToMin(endHm);
  return a === null || b === null ? null : b - a;
}

/** 'HH:MM' + minutes → 'HH:MM' on the same day (stops at 23:59). */
export function addMinutesToTime(hm, minutes) {
  const start = hmToMin(hm);
  if (start === null) return '';
  return minToHm(Math.min(23 * 60 + 59, start + (Number(minutes) || 0)));
}

/** The end time a job type implies from a start: its duration, else `fallback` (a working day by default). */
export function endTimeFor(service, startHm, fallback = DEFAULT_DAY_MINUTES) {
  return addMinutesToTime(startHm, service?.duration_minutes || fallback);
}

export const todayYmd = () => new Date().toLocaleDateString('en-CA', { timeZone: TZ });
export function addDaysYmd(ymd, n) {
  const d = new Date(`${ymd}T12:00:00+10:00`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toLocaleDateString('en-CA', { timeZone: TZ });
}
/** 0=Sun..6=Sat for a Brisbane date. */
export const dowYmd = (ymd) => new Date(`${ymd}T12:00:00+10:00`).getUTCDay();
/** The next Mon–Fri on or after `ymd`. */
export function nextWorkingDay(ymd) {
  let d = ymd;
  for (let i = 0; i < 7 && [0, 6].includes(dowYmd(d)); i++) d = addDaysYmd(d, 1);
  return d;
}

/** The days a span covers, as the server counts them (weekends optional). */
export function spanDates(startYmd, endYmd, skipWeekends = false) {
  const out = [];
  if (!startYmd || !endYmd || endYmd < startYmd) return out;
  let d = startYmd;
  for (let i = 0; i < 62 && d <= endYmd; i++) {
    const dow = dowYmd(d);
    if (!(skipWeekends && (dow === 0 || dow === 6))) out.push(d);
    d = addDaysYmd(d, 1);
  }
  return out;
}

export const fmtDayYmd = (ymd) => (ymd ? new Date(`${ymd}T12:00:00+10:00`).toLocaleDateString('en-AU', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short' }) : '…');
export const fmtHm = (hm) => {
  const m = hmToMin(hm);
  if (m === null) return '…';
  const h = Math.floor(m / 60);
  return `${h % 12 || 12}:${String(m % 60).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
};
export const fmtHours = (min) => {
  const h = Math.round((min / 60) * 10) / 10;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} h`;
};

/** What a span will book, in one line: "3 days · Fri, 11 Sept → Tue, 15 Sept · 8:00 am–4:00 pm each day (8 h)". */
export function describeSpan({ start_date, start_time, end_date, end_time, skip_weekends }) {
  const days = spanDates(start_date, end_date || start_date, skip_weekends);
  if (!days.length) return start_date && (end_date || start_date) >= start_date ? 'Every day in that span is a weekend — untick "Skip weekends" or pick other days' : 'Pick a start and an end date';
  const mins = minutesBetween(start_time, end_time);
  const perDay = mins > 0
    ? `${fmtHm(start_time)}–${fmtHm(end_time)}${days.length > 1 ? ' each day' : ''} (${fmtHours(mins)})`
    : `from ${fmtHm(start_time)}`;
  const when = days.length > 1 ? `${fmtDayYmd(days[0])} → ${fmtDayYmd(days[days.length - 1])}` : fmtDayYmd(days[0]);
  return `${days.length} day${days.length === 1 ? '' : 's'} · ${when} · ${perDay}`;
}

/** What happened to one day of a span, for the results list. */
export function spanResultLabel(x) {
  if (!x.ok) return x.error;
  if (x.existing) return 'already on the schedule';
  if (x.adjusted) return `moved to these times${x.note ? ` (${x.note})` : ''}`;
  return 'booked';
}
